import {
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
} from 'react';
import { Row } from 'react-table';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { Col } from '../Col/Col';

import { TableProps } from './Table';
import { TableDataCell, TableRow } from './Table.styles';

interface OwnProps<T extends object>
  extends ListChildComponentProps,
    Pick<TableProps<T>, 'isRowDisabled' | 'onRowClick' | 'expansionRender'> {
  rowHeight: number;
  rows: Row<T>[];
  prepareRow: (row: Row<T>) => void;
  setExpandedRowSizes: Dispatch<SetStateAction<Record<string, number>>>;
  expandedRowSizes: Record<string, number>;
  listRef: MutableRefObject<VariableSizeList<any> | null>;
}

const VirtualizedRow = <T extends object>({
  index,
  style,
  rows,
  listRef,
  expansionRender,
  prepareRow,
  isRowDisabled,
  onRowClick,
  setExpandedRowSizes,
  expandedRowSizes,
  rowHeight,
}: PropsWithChildren<OwnProps<T>>) => {
  const row = rows[index];

  prepareRow(row);

  const isDisabled = isRowDisabled?.(row.original);
  const expansionWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      row.isExpanded &&
      !expandedRowSizes[index] &&
      expansionWrapperRef.current
    ) {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }

      const expansionHeight = expansionWrapperRef.current.getBoundingClientRect()
        .height;

      setExpandedRowSizes((prevState) => ({
        ...prevState,
        [index]: expansionHeight,
      }));
    } else if (!row.isExpanded && expandedRowSizes[index]) {
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }

      setExpandedRowSizes((prevState) => ({ ...prevState, [index]: 0 }));
    }
  }, [index, row, expandedRowSizes, setExpandedRowSizes, listRef]);

  return (
    <Col
      {...row.getRowProps({
        style,
      })}
    >
      <TableRow
        role="row"
        onClick={() => onRowClick?.(row.original)}
        disabled={isDisabled}
        isClickable={!!onRowClick}
        height={rowHeight}
      >
        {row.cells.map((cell) => {
          const isExpanderCell = cell.column.id === 'expander';
          const isSelectableCell = cell.column.id === 'selection';

          return (
            <TableDataCell
              role="cell"
              isExpanderCell={isExpanderCell}
              isSelectableCell={isSelectableCell}
              {...cell.getCellProps()}
            >
              {cell.render('Cell')}
            </TableDataCell>
          );
        })}
      </TableRow>

      {row.isExpanded && (
        <TableRow role="row" ref={expansionWrapperRef}>
          {row.isExpanded && expansionRender?.(row.original)}
        </TableRow>
      )}
    </Col>
  );
};

export default VirtualizedRow;
